/** Sticky clickable TOC for downloaded Legend Sovereign case-file HTML. */

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function decodeEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

function plainHeading(html) {
  return decodeEntities(String(html || '').replace(/<[^>]+>/g, '')).trim();
}

export function sectionAnchorId(heading, used = {}) {
  const slug =
    String(heading || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  const base = `toc-${slug}`;
  const count = used[base] || 0;
  used[base] = count + 1;
  return count === 0 ? base : `${base}-${count + 1}`;
}

const STICKY_TOC_CSS = `
    html { scroll-behavior: smooth; }
    body { padding: 0; }
    .report-header {
      position: sticky;
      top: 0;
      z-index: 30;
      background: #0F1E38;
      border-bottom: 1px solid #B4893C;
      padding: 0.85rem 0 0.75rem;
    }
    .report-header .wrap { padding: 0 2rem; }
    .report-header h1 {
      color: #F5F7F9;
      font-size: 1.35rem;
      margin: 0;
    }
    .report-toc {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem 0.55rem;
      margin-top: 0.65rem;
    }
    .report-toc a {
      color: #0E7D7F;
      text-decoration: none;
      font-size: 0.78rem;
      line-height: 1.25;
      border: 1px solid #159A9C;
      border-radius: 4px;
      padding: 0.22rem 0.55rem;
      background: #F5F7F9;
    }
    .report-toc a:hover,
    .report-toc a:focus {
      color: #0F1E38;
      background: #7FC9CA;
    }
    .report-body { padding: 0.5rem 2rem 2.5rem; }
    a.toc-target {
      display: block;
      height: 0;
      scroll-margin-top: 7.5rem;
    }
    @media print {
      .report-header { position: static; }
      html { scroll-behavior: auto; }
    }
`;

export function ensureHtmlReportToc(html, title = '') {
  let updated = String(html || '');
  if (!updated.trim()) return updated;
  const used = {};
  const items = [];
  updated = updated.replace(
    /(?:<a class="toc-target"[^>]*><\/a>\s*)?(<section class="report-section">\s*<h2><strong>)(.*?)(<\/strong><\/h2>)/gi,
    (_full, prefix, headingHtml) => {
      const heading = plainHeading(headingHtml);
      const anchor = sectionAnchorId(heading, used);
      if (heading) items.push([heading, anchor]);
      return `<a class="toc-target" id="${escapeHtml(anchor)}"></a>\n<section class="report-section">\n  <h2><strong>${headingHtml}</strong></h2>`;
    }
  );
  if (!items.length) return updated;
  const toc = `<nav class="report-toc" aria-label="Case file sections">${items
    .map(([heading, anchor]) => `<a href="#${escapeHtml(anchor)}">${escapeHtml(heading)}</a>`)
    .join('')}</nav>`;
  const titleMatch = updated.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  const titleText = String(title || '').trim() || plainHeading(titleMatch?.[1] || '') || 'Tier 1 Analysis Report';
  const header = `<header class="report-header">
    <div class="wrap">
      <h1>${escapeHtml(titleText)}</h1>
      ${toc}
    </div>
  </header>
`;
  const cssMissing = !updated.includes('a.toc-target');
  if (/class=["']report-header["']/i.test(updated)) {
    updated = updated.replace(/<nav class="report-toc"[^>]*>[\s\S]*?<\/nav>/i, toc);
  } else if (/<body[^>]*>/i.test(updated)) {
    updated = updated.replace(/(<div class="wrap">\s*)<h1[^>]*>[\s\S]*?<\/h1>/i, '$1');
    updated = updated.replace(/<div class="wrap">/i, '<div class="wrap report-body">');
    updated = updated.replace(/<body[^>]*>/i, (open) => `${open}\n  ${header}`);
  }
  if (cssMissing) {
    if (updated.includes('</style>')) {
      updated = updated.replace('</style>', `${STICKY_TOC_CSS}\n  </style>`);
    } else {
      updated = updated.replace(/<\/head>/i, `<style>${STICKY_TOC_CSS}</style>\n</head>`);
    }
  }
  return updated;
}

const CASE_FILE_STICKY_TOC_CSS = `
    html { scroll-behavior: smooth; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      margin: 0;
      padding: 0;
      background: #F5F7F9;
      color: #0F1E38;
    }
    .report-header {
      position: sticky;
      top: 0;
      z-index: 30;
      background: #F5F7F9;
      border-bottom: 1px solid #B4893C;
      padding: 16px 20px 12px;
    }
    .report-header-inner { max-width: 1200px; margin: 0 auto; }
    .report-header h1 {
      font-size: 28px;
      font-weight: 700;
      margin: 0;
      color: #0F1E38;
    }
    .report-toc {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 8px;
      margin-top: 10px;
    }
    .report-toc a {
      color: #0E7D7F;
      text-decoration: none;
      font-size: 12px;
      line-height: 1.25;
      border: 1px solid #159A9C;
      border-radius: 4px;
      padding: 3px 8px;
      background: #FFFFFF;
    }
    .report-toc a:hover,
    .report-toc a:focus {
      background: #7FC9CA33;
    }
    .meta {
      color: #5B6675;
      font-size: 14px;
      margin-top: 8px;
    }
    .report-body {
      max-width: 1200px;
      margin: 0 auto;
      padding: 16px 20px 40px;
    }
    a.toc-target {
      display: block;
      height: 0;
      scroll-margin-top: 120px;
    }
    .section {
      background: #FFFFFF;
      border: 1px solid #d7dee6;
      border-radius: 8px;
      padding: 24px;
      margin-bottom: 24px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 16px;
      color: #0F1E38;
      border-bottom: 2px solid #159A9C;
      padding-bottom: 8px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      text-align: left;
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    th {
      background: #f3f4f6;
      font-weight: 600;
      color: #374151;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    td {
      font-size: 14px;
      vertical-align: top;
      white-space: pre-wrap;
    }
    .playbook-step {
      background: #F5F7F9;
      border-left: 4px solid #159A9C;
      padding: 16px;
      margin: 12px 0;
      page-break-inside: avoid;
    }
    .playbook-step h4 {
      margin: 0 0 12px 0;
      color: #0E7D7F;
      font-size: 15px;
    }
    .playbook-field { margin: 8px 0; font-size: 14px; }
    .playbook-label {
      font-weight: 600;
      color: #0F1E38;
      display: block;
      margin-bottom: 4px;
    }
    .playbook-value { color: #0F1E38; white-space: pre-wrap; }
    @media print {
      .report-header { position: static; }
      .report-toc { display: none; }
      html { scroll-behavior: auto; }
      body { margin: 0; padding: 20px; }
      .section { page-break-inside: avoid; }
    }
`;

function playbookField(label, value) {
  if (!value) return '';
  return `        <div class="playbook-field">
          <span class="playbook-label">${escapeHtml(label)}</span>
          <div class="playbook-value">${escapeHtml(value)}</div>
        </div>
`;
}

export function buildSovereignCaseFileHtml(parsed, title, extras = {}) {
  const sections = parsed?.sections || [];
  const investigatorNotes = extras.investigatorNotes || extras.investigator_notes || '';
  const sectionNotes = extras.sectionNotes || extras.section_notes || {};
  const heading = String(title || '').trim() || 'Sovereign Assessment';
  const used = {};
  const tocItems = sections.map((section) => {
    const name = section.title || 'Section';
    return { heading: name, anchor: sectionAnchorId(name, used) };
  });
  if (String(investigatorNotes).trim()) {
    tocItems.push({ heading: 'Investigator Case Notes', anchor: 'toc-investigator-case-notes' });
  }
  const toc = tocItems
    .map((item) => `<a href="#${escapeHtml(item.anchor)}">${escapeHtml(item.heading)}</a>`)
    .join('');
  let body = '';
  sections.forEach((section, idx) => {
    const item = tocItems[idx];
    body += `  <div class="section">
    <a class="toc-target" id="${escapeHtml(item.anchor)}"></a>
    <h2 class="section-title">${escapeHtml(section.title || 'Section')}</h2>
`;
    if (section.kind === 'prose' && section.paragraphs?.length) {
      section.paragraphs.forEach((paragraph) => {
        body += `    <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px;">${escapeHtml(paragraph)}</p>\n`;
      });
    } else if (section.kind === 'file-list') {
      if (section.files?.length) {
        body += '    <ul>\n';
        section.files.forEach((name) => {
          body += `      <li>${escapeHtml(name)}</li>\n`;
        });
        body += '    </ul>\n';
      } else {
        body += '    <p>No files were provided for this assessment.</p>\n';
      }
    }
    if (section.playbook?.length) {
      section.playbook.forEach((step) => {
        body += `      <div class="playbook-step">
        <h4>Step ${escapeHtml(step.step_number || '')}</h4>
${playbookField('What You Do:', step.what_you_do || '')}${playbookField('What You Say:', step.what_you_say || '')}${playbookField('What to Listen For:', step.what_to_listen_for || '')}${playbookField('If Pushback:', step.if_pushback || '')}${playbookField('Abort Conditions:', step.abort_conditions || '')}      </div>
`;
      });
    }
    if (section.targets?.length) {
      body += `    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Name</th>
          <th>Role</th>
          <th>Relationship</th>
          <th>Elicitation angle</th>
        </tr>
      </thead>
      <tbody>
`;
      section.targets.forEach((target) => {
        body += `        <tr>
          <td>${escapeHtml(target.num || '')}</td>
          <td>${escapeHtml(target.name || '')}</td>
          <td>${escapeHtml(target.role || '')}</td>
          <td>${escapeHtml(target.relationship || '')}</td>
          <td>${escapeHtml(target.angle || '')}</td>
        </tr>
`;
      });
      body += `      </tbody>
    </table>
`;
    } else if (section.rows?.length) {
      body += `    <table>
      <thead>
        <tr>
          <th style="width: 30%">Heading / Fact</th>
          <th style="width: 55%">Content</th>
          <th style="width: 15%">Source</th>
        </tr>
      </thead>
      <tbody>
`;
      section.rows.forEach((row) => {
        body += `        <tr>
          <td>${escapeHtml(row.heading || '')}</td>
          <td>${escapeHtml(row.content || '')}</td>
          <td>${escapeHtml(row.source || 'Analysis')}</td>
        </tr>
`;
      });
      body += `      </tbody>
    </table>
`;
    }
    const note = sectionNotes[section.title];
    if (String(note || '').trim()) {
      body += `    <div class="playbook-step" style="background: #fef3c7; border-left-color: #f59e0b;">
      <h4 style="color: #d97706;">Investigator Notes: ${escapeHtml(section.title)}</h4>
      <div class="playbook-value">${escapeHtml(note)}</div>
    </div>
`;
    }
    body += `  </div>
`;
  });
  if (String(investigatorNotes).trim()) {
    body += `  <div class="section">
    <a class="toc-target" id="toc-investigator-case-notes"></a>
    <h2 class="section-title" style="color: #d97706; border-bottom-color: #f59e0b;">Investigator Case Notes</h2>
    <div class="playbook-step" style="background: #fef3c7; border-left-color: #f59e0b;">
      <div class="playbook-value">${escapeHtml(investigatorNotes)}</div>
    </div>
  </div>
`;
  }
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(heading)}</title>
  <style>${CASE_FILE_STICKY_TOC_CSS}
  </style>
</head>
<body>
  <header class="report-header">
    <div class="report-header-inner">
      <h1>${escapeHtml(heading)}</h1>
      <nav class="report-toc" aria-label="Case file sections">${toc}</nav>
      <div class="meta">Investigator Case File - ${escapeHtml(new Date().toLocaleDateString())}</div>
    </div>
  </header>
  <div class="report-body">
${body}  </div>
</body>
</html>`;
}
